// @givmo/ai — the WEB half of the assistant route registry's per-platform mapping
// (contract §1.4 L5; backend `route_registry.web_url()` / `donate_web_host()`).
//
// An `action_chips` chip carries a server-validated `ValidatedRoute` (never a
// model URL — trust posture §3.2). The web client resolves the navigable URL from
// those canonical fields HERE. This mirrors the backend `web_url()` exactly:
//
//   - Only the **donate** surface resolves today. Its URLs are byte-pinned because
//     shipped iOS binaries + printed QR codes depend on them:
//       https://{donate_host}/?{charityId|etfId|fundraiserId}={target_id}
//     env-aware host (prod → donate.givmo.io, staging → donate-staging.givmo.io,
//     else donate-dev.givmo.io).
//   - Every other route_type (follow_charity / setup_recurring / open_receipt /
//     open_screen) returns `null` — its web destination lives on the consumer web
//     app (08) / the 04 receipt host, neither shipped. A chip whose route resolves
//     to `null` is INERT (rendered non-navigable), never an invented URL.
//
// `target_id` is server-sanitized to `[A-Za-z0-9_-]{1,128}`, so raw interpolation
// into the query carries no URL-special byte that could split it. The web client
// MUST NOT relax that assumption — but we additionally re-validate defensively
// here (a wire value that somehow escaped the gate must not produce a navigable
// URL with a smuggled query/path).
import type { RouteTargetKind, ValidatedRoute } from '../contract/routes';

/** The backend `GIVMO_ENV` values that select a donate host. */
export type GivmoEnv = 'local' | 'dev' | 'staging' | 'prod' | (string & {});

/** The donate web app host for `env` (mirrors backend `donate_web_host()`). These
 * hosts are byte-pinned (printed QR codes carry the prod host forever). */
export function donateWebHost(env: GivmoEnv): string {
  if (env === 'prod') return 'donate.givmo.io';
  if (env === 'staging') return 'donate-staging.givmo.io';
  return 'donate-dev.givmo.io'; // local / dev / anything else
}

/** Server-sanitization mirror: `[A-Za-z0-9_-]{1,128}`, full-match (defense in
 * depth — the backend already gate-sanitized this). */
const ID_RE = /^[A-Za-z0-9_-]{1,128}$/;

const DONATE_PARAM_BY_KIND: Partial<Record<RouteTargetKind, string>> = {
  charity: 'charityId',
  etf: 'etfId',
  fundraiser: 'fundraiserId',
};

/**
 * Resolve a validated route to its web URL, or `null` when there is no pinned web
 * destination (→ the chip is inert). Mirrors backend `web_url()`.
 */
export function resolveWebUrl(route: ValidatedRoute, env: GivmoEnv): string | null {
  if (route.route_type !== 'donate') return null;

  const param = DONATE_PARAM_BY_KIND[route.target_kind];
  const targetId = route.target_id;
  if (param === undefined || !targetId) return null;

  // Defensive re-validation (the backend already sanitized — a wire value that
  // failed the regex must not yield a navigable URL).
  if (!ID_RE.test(targetId)) return null;

  return `https://${donateWebHost(env)}/?${param}=${targetId}`;
}
